// ─────────────────────────────────────────────────────────────────
// Glass-i — Use Glass Concept in Quotation (Stage 6B.8A-3J polish).
//
// Operator-facing surface. The quotation/proforma module is not yet
// available — this drawer prepares the payload, shows three separate
// preview grids (Glass / Hardware / Service) that align with the
// future Proforma / Tax Invoice line layout, surfaces a compact
// summary panel + warnings, and exposes both the JSON payload and a
// copy action.
//
// What "proforma-style polish" means here:
//   • header form on top (mandatory product description + W × H)
//   • Glass Lines preview grid has a dedicated Fabrication column
//     and the description / size columns that a proforma row needs
//   • Hardware grid renders default brand + override brand + the
//     replacement-item picker side-by-side
//   • Service grid is read-only with the same compact density
//   • Summary panel on the right shows glass / hardware / service
//     counts, total qty and a fabrication breakdown
// ─────────────────────────────────────────────────────────────────

import { useEffect, useState, useMemo } from 'react';
import {
  Drawer, Form, Input, InputNumber, Switch, Button, Table, Tag,
  Alert, Space, Tabs, message, Skeleton, Divider, Card, Tooltip
} from 'antd';
import {
  ToolOutlined, ReloadOutlined, CopyOutlined, CloseOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { glassConceptsApi } from '../glassConceptsApi';
import { itemsApi } from '../../items/itemsApi';

// ── Replacement-item picker (compact native select; quotation surfaces
// stay light, no big AntD Select per row). ───────────────────────
function ReplacementItemPicker({ value, onChange, disabled }) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const load = async (q) => {
    setLoading(true);
    try {
      const result = await itemsApi.lookup({
        q: q || '', status: 'active', limit: 30
      });
      const rows = Array.isArray(result)
        ? result
        : (result && Array.isArray(result.rows) ? result.rows : []);
      setOptions(rows.map((r) => ({
        value: r.id,
        label: r.itemName || r.id,
        code:  r.itemCode || ''
      })));
    } catch (_e) { setOptions([]); } finally { setLoading(false); }
  };
  useEffect(() => { load(''); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    if (!value) return;
    if (options.some((o) => o.value === value)) return;
    itemsApi.get(value).then((row) => {
      if (!row) return;
      setOptions((prev) => prev.some((o) => o.value === row.id)
        ? prev
        : [...prev, { value: row.id, label: row.itemName, code: row.itemCode }]);
    }).catch(() => {});
  }, [value, options]);
  return (
    <select
      value={value || ''}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value || null)}
      style={{
        width: '100%', height: 24, fontSize: 12, border: '1px solid #d9d9d9',
        borderRadius: 4, padding: '0 6px', background: disabled ? '#f5f5f5' : 'white'
      }}
    >
      <option value="">— No replacement —</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.code ? `${o.code} · ` : ''}{o.label}
        </option>
      ))}
    </select>
  );
}

// ── Tiny KV row helper for the summary panel ─────────────────────
function Kv({ label, value, accent }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '4px 0', borderBottom: '1px dashed #e2e8f0'
    }}>
      <span style={{ fontSize: 11.5, color: '#64748b' }}>{label}</span>
      <span style={{
        fontSize: 12.5, fontWeight: 600,
        color: accent === 'warn' ? '#b45309' : '#0f172a',
        fontVariantNumeric: 'tabular-nums'
      }}>{value}</span>
    </div>
  );
}

// ── Code chip used in many cells ─────────────────────────────────
function CodeChip({ children }) {
  if (!children) return <span style={{ color: '#cbd5e1' }}>—</span>;
  return (
    <code style={{
      fontSize: 10.5, color: '#475569', background: '#f1f5f9',
      padding: '1px 6px', borderRadius: 3, fontWeight: 500
    }}>{children}</code>
  );
}

export default function UseInQuotationDrawer({
  open, conceptId = null, onClose
}) {
  const [form] = Form.useForm();
  const [loading, setLoading]       = useState(false);
  const [building, setBuilding]     = useState(false);
  const [concept, setConcept]       = useState(null);
  const [panels, setPanels]         = useState([]); // concept panel rows
  const [hardware, setHardware]     = useState([]); // concept hardware rows
  const [services, setServices]     = useState([]); // concept service rows
  const [overrides, setOverrides]   = useState({}); // {lineId: {brand, replacementItemId}}
  const [payload, setPayload]       = useState(null);
  const [error, setError]           = useState(null);

  // ── load concept on open ─────────────────────────────────────
  useEffect(() => {
    if (!open || !conceptId) {
      setConcept(null); setPanels([]); setHardware([]); setServices([]);
      setOverrides({}); setPayload(null); setError(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true); setError(null);
    glassConceptsApi.get(conceptId)
      .then((data) => {
        if (cancelled || !data) return;
        setConcept(data.concept);
        setPanels(data.panels    || []);
        setHardware(data.hardware || []);
        setServices(data.services || []);
        form.setFieldsValue({
          widthMm:  data.concept.defaultWidthMm  || undefined,
          heightMm: data.concept.defaultHeightMm || undefined,
          depthMm:  data.concept.defaultDepthMm  || undefined,
          productDescription: '',
          includeServices: true,
          quantityMultiplier: 1
        });
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e?.response?.data?.error?.message
            || e.message || 'Failed to load concept.');
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, conceptId, form]);

  const handleBuild = async () => {
    let body;
    try {
      body = await form.validateFields();
    } catch (_e) { return; }
    const hardwareBrandOverrides = Object.entries(overrides)
      .map(([hardwareLineId, o]) => ({
        hardwareLineId,
        brand:             o.brand || null,
        replacementItemId: o.replacementItemId || null
      }))
      .filter((o) => o.brand || o.replacementItemId);
    setBuilding(true); setError(null);
    try {
      const out = await glassConceptsApi.buildQuotationPayload(conceptId, {
        ...body, hardwareBrandOverrides
      });
      setPayload(out);
    } catch (e) {
      setError(e?.response?.data?.error?.message
        || e.message || 'Failed to build quotation payload.');
    } finally {
      setBuilding(false);
    }
  };

  const copyJson = async () => {
    if (!payload) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
      message.success('Payload copied to clipboard.');
    } catch (_e) {
      message.error('Clipboard not available — open the JSON tab to copy manually.');
    }
  };

  const updateOverride = (lineId, patch) => setOverrides((prev) => ({
    ...prev, [lineId]: { ...(prev[lineId] || {}), ...patch }
  }));

  // ── Derived summary metrics ──────────────────────────────────
  const summary = useMemo(() => {
    if (!payload) return null;
    const glassLines    = payload.lines.filter((l) => l.lineType === 'glass');
    const hardwareLines = payload.lines.filter((l) => l.lineType === 'hardware');
    const serviceLines  = payload.lines.filter((l) => l.lineType === 'service');
    const fabCounts = {};
    for (const g of glassLines) {
      const key = g.fabrication || '(none)';
      fabCounts[key] = (fabCounts[key] || 0) + 1;
    }
    return {
      glassCount:     glassLines.length,
      hardwareCount:  hardwareLines.length,
      serviceCount:   serviceLines.length,
      glassQty:       glassLines.reduce((s, l) => s + Number(l.qty || 0), 0),
      hardwareQty:    hardwareLines.reduce((s, l) => s + Number(l.qty || 0), 0),
      serviceQty:     serviceLines.reduce((s, l) => s + Number(l.qty || 0), 0),
      fabCounts,
      warningCount:   (payload.warnings || []).length
    };
  }, [payload]);

  // ── Column defs ──────────────────────────────────────────────
  const glassColumns = [
    { title: 'Line', width: 44, align: 'center',
      render: (_v, _r, i) => <span style={{ color: '#94a3b8' }}>{i + 1}</span> },
    { title: 'Panel', dataIndex: 'panelName', width: 130,
      render: (v) => v || <span style={{ color: '#cbd5e1' }}>—</span> },
    { title: 'Glass Product', dataIndex: 'glassProduct',
      render: (_v, r) => (
        <div>
          <div style={{ fontWeight: 500, color: '#0f172a' }}>{r.glassItemName || '—'}</div>
          {r.glassItemCode ? <CodeChip>{r.glassItemCode}</CodeChip> : null}
        </div>
      ) },
    { title: 'Thick', dataIndex: 'thicknessMm', width: 60, align: 'right',
      render: (v) => v != null ? v : <span style={{ color: '#cbd5e1' }}>—</span> },
    { title: 'W (mm)', dataIndex: 'widthMm', width: 70, align: 'right' },
    { title: 'H (mm)', dataIndex: 'heightMm', width: 70, align: 'right' },
    { title: 'Qty',  dataIndex: 'qty',  width: 64, align: 'right',
      render: (v) => Number(v || 0).toFixed(2) },
    { title: 'UOM', dataIndex: 'uom', width: 60, align: 'center',
      render: (v) => <CodeChip>{v}</CodeChip> },
    { title: 'Fabrication', dataIndex: 'fabrication', width: 160,
      render: (v) => v
        ? <Tag color="purple" style={{ margin: 0, fontSize: 10.5 }}>{v}</Tag>
        : <span style={{ color: '#cbd5e1', fontSize: 11 }}>No Cutout</span> },
    { title: 'Description', dataIndex: 'description',
      ellipsis: true,
      render: (v) => v || <span style={{ color: '#cbd5e1' }}>—</span> }
  ];

  const hardwareColumns = [
    { title: 'Line', width: 44, align: 'center',
      render: (_v, _r, i) => <span style={{ color: '#94a3b8' }}>{i + 1}</span> },
    { title: 'Hardware', dataIndex: 'hardwareItemName',
      render: (v, r) => (
        <div>
          <div style={{ fontWeight: 500, color: '#0f172a' }}>
            {v || r.hardwareNameSnapshot || r.hardwareRole || '—'}
          </div>
          {r.hardwareItemCode ? <CodeChip>{r.hardwareItemCode}</CodeChip> : null}
        </div>
      ) },
    { title: 'Role', dataIndex: 'hardwareRole', width: 130,
      render: (v) => v || <span style={{ color: '#cbd5e1' }}>—</span> },
    { title: 'Default Brand', dataIndex: 'defaultBrand', width: 120,
      render: (v) => v
        ? <Tag color="default" style={{ margin: 0, fontSize: 10.5 }}>{v}</Tag>
        : <span style={{ color: '#cbd5e1' }}>—</span> },
    { title: 'Brand Override', width: 120,
      render: (_v, r) => (
        <Input size="small" placeholder="Type to override"
          value={(overrides[r.id] && overrides[r.id].brand) || ''}
          disabled={r.allowBrandOverride === false}
          onChange={(e) => updateOverride(r.id, { brand: e.target.value })} />
      ) },
    { title: 'Replacement Item', width: 210,
      render: (_v, r) => (
        <ReplacementItemPicker
          value={(overrides[r.id] && overrides[r.id].replacementItemId) || null}
          disabled={r.allowBrandOverride === false}
          onChange={(picked) => updateOverride(r.id, { replacementItemId: picked })} />
      ) },
    { title: 'Qty', dataIndex: 'qty', width: 60, align: 'right',
      render: (v) => Number(v || 0).toFixed(2) },
    { title: 'UOM', dataIndex: 'uomCode', width: 56, align: 'center',
      render: (v) => <CodeChip>{v}</CodeChip> },
    { title: 'Req?', dataIndex: 'isRequired', width: 60, align: 'center',
      render: (v) => v
        ? <Tag color="red" style={{ margin: 0, fontSize: 10 }}>Yes</Tag>
        : <Tag color="default" style={{ margin: 0, fontSize: 10 }}>No</Tag> }
  ];

  const serviceColumns = [
    { title: 'Line', width: 44, align: 'center',
      render: (_v, _r, i) => <span style={{ color: '#94a3b8' }}>{i + 1}</span> },
    { title: 'Service', dataIndex: 'serviceItemName',
      render: (v, r) => v || r.serviceNameSnapshot || r.serviceRole || '—' },
    { title: 'Role', dataIndex: 'serviceRole', width: 200,
      render: (v) => v || <span style={{ color: '#cbd5e1' }}>—</span> },
    { title: 'Qty', dataIndex: 'qty', width: 60, align: 'right',
      render: (v) => Number(v || 0).toFixed(2) },
    { title: 'UOM', dataIndex: 'uomCode', width: 56, align: 'center',
      render: (v) => <CodeChip>{v}</CodeChip> },
    { title: 'Remarks', dataIndex: 'remarks', ellipsis: true,
      render: (v) => v || <span style={{ color: '#cbd5e1' }}>—</span> }
  ];

  const previewColumns = [
    { title: '#', width: 30, align: 'center',
      render: (_v, _r, i) => <span style={{ color: '#94a3b8' }}>{i + 1}</span> },
    { title: 'Type', dataIndex: 'lineType', width: 78,
      render: (v) => <Tag
        color={v === 'glass' ? 'blue' : v === 'hardware' ? 'geekblue' : 'default'}
        style={{ margin: 0, fontSize: 10 }}>{String(v).toUpperCase()}</Tag> },
    { title: 'Description', dataIndex: 'description', ellipsis: true },
    { title: 'Brand', dataIndex: 'brand', width: 90,
      render: (v) => v || <span style={{ color: '#cbd5e1' }}>—</span> },
    { title: 'Fabrication', dataIndex: 'fabrication', width: 140,
      render: (v) => v
        ? <Tag color="purple" style={{ margin: 0, fontSize: 10 }}>{v}</Tag>
        : <span style={{ color: '#cbd5e1' }}>—</span> },
    { title: 'Qty', dataIndex: 'qty', width: 64, align: 'right',
      render: (v) => Number(v).toFixed(2) },
    { title: 'UOM', dataIndex: 'uom', width: 58, align: 'center',
      render: (v) => <CodeChip>{v}</CodeChip> }
  ];

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={1200}
      destroyOnClose
      title={
        <Space>
          <ToolOutlined />
          <span>Use Glass Concept</span>
          {concept ? (
            <Tag color="blue" style={{ marginInlineStart: 8 }}>
              {concept.conceptCode} · {concept.conceptName}
            </Tag>
          ) : null}
        </Space>
      }
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button icon={<CloseOutlined />} onClick={onClose}>Close</Button>
          <Space>
            {payload ? (
              <Button icon={<CopyOutlined />} onClick={copyJson}>
                Copy Payload JSON
              </Button>
            ) : null}
            <Button type="primary" icon={<ReloadOutlined />}
              loading={building} onClick={handleBuild}
              disabled={!conceptId || loading}>
              Build Quotation Lines
            </Button>
          </Space>
        </div>
      }
    >
      {error ? (
        <Alert type="error" message="Build failed" description={error}
          style={{ marginBottom: 12 }} />
      ) : null}

      {loading ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 12 }}>
          {/* ── LEFT column: header form + grids + preview ────────── */}
          <div>
            <Card size="small" style={{ marginBottom: 10 }}
              styles={{ body: { padding: '10px 12px' } }}
              title={<span style={{ fontSize: 12.5, fontWeight: 600 }}>Quotation Header</span>}>
              <Form form={form} layout="vertical" requiredMark="optional"
                size="small">
                <Form.Item label="Product Description" name="productDescription" required
                  rules={[{ required: true,
                    message: 'Product description is required before building quotation lines.' }]}
                  extra="This description will be used in quotation / proforma lines. Confirm thickness, glass type, process and fitting details."
                  style={{ marginBottom: 10 }}>
                  <Input placeholder="Example: 12MM Toughened Glass Door with Patch Fittings" />
                </Form.Item>
                <div style={{ display: 'grid',
                  gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr', gap: 8 }}>
                  <Form.Item label="Width (mm)" name="widthMm" required
                    rules={[{ required: true, message: 'Width is required.' }]}>
                    <InputNumber min={0} style={{ width: '100%' }} />
                  </Form.Item>
                  <Form.Item label="Height (mm)" name="heightMm" required
                    rules={[{ required: true, message: 'Height is required.' }]}>
                    <InputNumber min={0} style={{ width: '100%' }} />
                  </Form.Item>
                  <Form.Item label="Depth (mm)" name="depthMm">
                    <InputNumber min={0} style={{ width: '100%' }} />
                  </Form.Item>
                  <Form.Item label="Qty ×" name="quantityMultiplier">
                    <InputNumber min={0.0001} style={{ width: '100%' }} />
                  </Form.Item>
                  <Form.Item label="Services?" name="includeServices"
                    valuePropName="checked">
                    <Switch checkedChildren="Yes" unCheckedChildren="No" />
                  </Form.Item>
                </div>
              </Form>
            </Card>

            {/* Glass Lines preview grid */}
            <Card size="small" style={{ marginBottom: 10 }}
              styles={{ body: { padding: 0 } }}
              title={
                <div style={{ display: 'flex', justifyContent: 'space-between',
                              alignItems: 'center' }}>
                  <span style={{ fontSize: 12.5, fontWeight: 600 }}>
                    Glass Lines{' '}
                    <Tag color="blue" style={{ marginLeft: 4 }}>{panels.length}</Tag>
                  </span>
                </div>}>
              <Table size="small" rowKey="id"
                dataSource={panels} columns={glassColumns}
                pagination={false} scroll={{ x: 'max-content' }}
                locale={{ emptyText: 'This concept has no glass panels.' }} />
            </Card>

            {/* Hardware BOQ grid */}
            <Card size="small" style={{ marginBottom: 10 }}
              styles={{ body: { padding: 0 } }}
              title={
                <div style={{ display: 'flex', justifyContent: 'space-between',
                              alignItems: 'center' }}>
                  <span style={{ fontSize: 12.5, fontWeight: 600 }}>
                    Hardware BOQ{' '}
                    <Tag color="geekblue" style={{ marginLeft: 4 }}>{hardware.length}</Tag>
                  </span>
                  <span style={{ fontSize: 11, color: '#64748b',
                    display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <InfoCircleOutlined />
                    Change brand or replacement item only when required.
                  </span>
                </div>}>
              <Table size="small" rowKey="id"
                dataSource={hardware} columns={hardwareColumns}
                pagination={false} scroll={{ x: 'max-content' }}
                locale={{ emptyText: 'This concept has no hardware lines.' }} />
            </Card>

            {/* Service Lines grid */}
            <Card size="small" style={{ marginBottom: 10 }}
              styles={{ body: { padding: 0 } }}
              title={
                <span style={{ fontSize: 12.5, fontWeight: 600 }}>
                  Service Lines{' '}
                  <Tag color="default" style={{ marginLeft: 4 }}>{services.length}</Tag>
                </span>}>
              <Table size="small" rowKey="id"
                dataSource={services} columns={serviceColumns}
                pagination={false} scroll={{ x: 'max-content' }}
                locale={{ emptyText: 'No service lines on this concept.' }} />
            </Card>

            {/* Preview after build */}
            {payload ? (
              <>
                <Divider style={{ margin: '12px 0 8px' }}>
                  Quotation Preview ({payload.lines.length}{' '}
                  line{payload.lines.length === 1 ? '' : 's'})
                </Divider>
                {payload.warnings && payload.warnings.length > 0 ? (
                  <Alert
                    type="warning" showIcon
                    style={{ marginBottom: 8 }}
                    message={`${payload.warnings.length} warning${payload.warnings.length === 1 ? '' : 's'}`}
                    description={
                      <ul style={{ margin: 0, paddingInlineStart: 18 }}>
                        {payload.warnings.map((w, i) => (
                          <li key={i} style={{ fontSize: 12 }}>{w.message}</li>
                        ))}
                      </ul>
                    } />
                ) : null}
                <Tabs
                  size="small"
                  items={[
                    {
                      key: 'lines',
                      label: 'Quotation Lines',
                      children: (
                        <Table size="small" rowKey={(_r, i) => `l-${i}`}
                          dataSource={payload.lines} columns={previewColumns}
                          pagination={false} scroll={{ x: 'max-content' }} />
                      )
                    },
                    {
                      key: 'warnings',
                      label: <span>Warnings{payload.warnings && payload.warnings.length > 0 ? (
                        <Tag color="warning" style={{ marginLeft: 6 }}>{payload.warnings.length}</Tag>
                      ) : null}</span>,
                      children: payload.warnings && payload.warnings.length > 0 ? (
                        <ul style={{ margin: 0, paddingInlineStart: 18 }}>
                          {payload.warnings.map((w, i) => (
                            <li key={i} style={{ fontSize: 12, marginBottom: 4 }}>
                              <Tag color="warning" style={{ fontSize: 10 }}>{w.code}</Tag>
                              {' '}{w.message}
                            </li>
                          ))}
                        </ul>
                      ) : <span style={{ color: '#64748b', fontSize: 12 }}>No warnings.</span>
                    },
                    {
                      key: 'json',
                      label: 'Payload JSON',
                      children: (
                        <pre style={{
                          fontSize: 11, background: '#0f172a', color: '#e2e8f0',
                          padding: 12, borderRadius: 6, maxHeight: 320,
                          overflow: 'auto', margin: 0
                        }}>{JSON.stringify(payload, null, 2)}</pre>
                      )
                    }
                  ]} />
                <Alert
                  type="info" showIcon style={{ marginTop: 8 }}
                  message="Quotation / Proforma Invoice module is not enabled yet."
                  description="When that module ships, these prepared lines will be passed straight into it — no operator re-typing needed. Panel-specific scaling formulas are deferred." />
              </>
            ) : null}
          </div>

          {/* ── RIGHT column: summary panel ─────────────────────── */}
          <div>
            <Card size="small" style={{ position: 'sticky', top: 0 }}
              styles={{ body: { padding: '10px 12px' } }}
              title={<span style={{ fontSize: 12.5, fontWeight: 600 }}>Summary</span>}>
              {!summary ? (
                <div style={{ fontSize: 12, color: '#64748b', padding: '8px 0' }}>
                  Click <strong>Build Quotation Lines</strong> to compute the summary.
                </div>
              ) : (
                <>
                  <Kv label="Glass lines"      value={summary.glassCount}    />
                  <Kv label="Hardware lines"   value={summary.hardwareCount} />
                  <Kv label="Service lines"    value={summary.serviceCount}  />
                  <Kv label="Total glass qty"  value={summary.glassQty.toFixed(2)}    />
                  <Kv label="Total hardware qty" value={summary.hardwareQty.toFixed(2)} />
                  <Kv label="Warnings"
                    value={summary.warningCount}
                    accent={summary.warningCount > 0 ? 'warn' : undefined} />
                  <div style={{ marginTop: 8 }}>
                    <div style={{ fontSize: 11, color: '#64748b',
                      textTransform: 'uppercase', letterSpacing: 0.5,
                      marginBottom: 4 }}>
                      Fabrication breakdown
                    </div>
                    {Object.keys(summary.fabCounts).length === 0 ? (
                      <span style={{ fontSize: 12, color: '#94a3b8' }}>—</span>
                    ) : (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {Object.entries(summary.fabCounts).map(([k, n]) => (
                          <Tooltip key={k} title={`${n} glass line${n === 1 ? '' : 's'}`}>
                            <Tag color={k === '(none)' ? 'default' : 'purple'}
                              style={{ margin: 0, fontSize: 10.5 }}>
                              {k} × {n}
                            </Tag>
                          </Tooltip>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </Card>
          </div>
        </div>
      )}
    </Drawer>
  );
}
